import { Project, ProjectForResolver } from "db/definitions";
import { combine2StringArrays } from "utils/combine2Arrays";
import { db } from "../db";
import { buildFormGroupDownToFormInstances, deleteFormGroup, getFormGroupsByProjectId } from "./formGroupQueries";

/**
 * Given an array of project documents, saves them to the indexeddb
 * @author Eric Webb <ewebb@factorearth.com>
 * @param projects The projects we want to save
 */
export async function bulkSaveProjects(projects: ProjectForResolver[]): Promise<void> {
	await db.project.bulkPut(projects);
}

/**
 * Given the project document, saves it to the indexedDb
 * @author Eric Webb <ewebb@factorearth.com>
 * @param project The project we want to save
 */
export async function createProject(project: ProjectForResolver): Promise<void> {
	await db.project.put(project);
}

/**
 * Given the project, removes all the other document references and returns it
 * @author Eric Webb <ewebb@factorearth.com>
 * @param {Object} projectObj The project
 * @returns A parsed version of the project, with only the document specific properties
 */
export function createProjectForPut(projectObj: Project | ProjectForResolver): ProjectForResolver {
	const project: ProjectForResolver = {
		id: projectObj.id,
		name: projectObj.name,
		description: projectObj.description || "",
		public: projectObj.public,
		users: projectObj.users,
		group: projectObj.group,
		createdAt: projectObj.createdAt,
		updatedAt: projectObj.updatedAt,
	};
	return project;
}

/**
 * Given 2 project documents, merges them based off of the updatedAt times
 * @author Eric Webb <ewebb@factorearth.com>
 * @param donor The project from the donor source
 * @param receiver The project from the receiver (destination)
 * @returns The merged project
 */
export function createMergedProjectForPut(donor: ProjectForResolver, receiver: ProjectForResolver): ProjectForResolver {
	const users = combine2StringArrays(donor.users, receiver.users);
	const defaultDate = new Date();
	const donorUpdatedTime = donor.updatedAt ? new Date(donor.updatedAt) : defaultDate;
	const receiverUpdatedTime = receiver.updatedAt ? new Date(receiver.updatedAt) : defaultDate;
	const project: ProjectForResolver = {
		id: donor.id || receiver.id,
		name: donorUpdatedTime >= receiverUpdatedTime ? donor.name : receiver.name,
		description: donorUpdatedTime >= receiverUpdatedTime ? donor.description : receiver.description || "",
		public: donorUpdatedTime >= receiverUpdatedTime ? donor.public : receiver.public,
		users: users, // Not the most efficient way to do this I imagine, but creates one array of no duplicates,
		group: donorUpdatedTime >= receiverUpdatedTime ? donor.group : receiver.group,
		createdAt: donor.createdAt || receiver.createdAt || defaultDate.toISOString(),
		updatedAt: (donorUpdatedTime >= receiverUpdatedTime ? donor.updatedAt : receiver.updatedAt) || defaultDate.toISOString(), // If the stuff in parentheses ends up being nullish
	};
	return project;
}

/**
 * Given a project, deletes all formGroups by the project id, as well as the project itself
 * OFFLINE
 * @author Eric Webb <ewebb@factorearth.com>
 * @param {Object} project The project we're trying to delete
 */
export async function deleteProject(project: Project) {
	const deletePromises = [];
	if (project.formGroups?.items) {
		for (const formGroup of project.formGroups.items) {
			deletePromises.push(deleteFormGroup(formGroup));
		}
	}
	deletePromises.push(deleteProjectDoc(project.id));
	await Promise.all(deletePromises);
}

/**
 * Given the id of a project, deletes that document from the indexedDb
 * @author Eric Webb <ewebb@factorearth.com>
 * @param id The id of the project we want to delete
 */
export async function deleteProjectDoc(id: string) {
	await db.project.delete(id);
}

/**
 * Given the project id, queries all child documents and builds the project out to the formInstance level
 * @author Eric Webb <ewebb@factorearth.com>
 * @param id The project id
 * @returns A project, built to the formInstace level
 */
export async function getProjectAndFormGroupsForSyncTab(id: string): Promise<Project | null> {
	// This is getting stuff from the indexedDb. We need to get all of the project data with the nested stuff
	// Step 1: Get the project by id
	// Step 2: Get all formGroups by project id
	// Step 3: Await promises
	// Step 4: Create promise array for the completely built formGroups
	// Step 5: For each formGroup from step 3, build the formGroups
	// Step 6: Await promises
	// Step 7: Attach builtFormGroups to project, and return with fields. Determine if the db has the project, and if it does, return it. Otherwise, return null

	// Step 1, 2, and 3: Get project + formGroups, Await promises
	const [project, formGroups] = await Promise.all([getProjectById(id), getFormGroupsByProjectId(id)]);

	// Step 4: Promise array for the built formGroups
	const builtFormGroupPromises = [];

	// Step 5: For each formGroup, get all formInstances by formGroup id
	for (let i = 0; i < formGroups.length; i++) {
		builtFormGroupPromises.push(buildFormGroupDownToFormInstances(formGroups[i]));
	}

	// Step 6: Await formInstance promises
	const builtFormGroups = await Promise.all(builtFormGroupPromises);

	// Step 7: FormGroups are done, attach to project, and return. Determine if the db has the project, and if it does, return it. Otherwise, return null
	if (project) {
		const finalProject: Project = {
			...project,
			formGroups: { items: builtFormGroups },
		};
		return finalProject;
	}
	return null;
}

/**
 * Given the id of a project, queries and returns the document
 * @author Eric Webb <ewebb@factorearth.com>
 * @param id The id of the projectq
 * @returns The project document
 */
export async function getProjectById(id: string): Promise<ProjectForResolver | undefined> {
	const project = await db.project.get(id);
	return project;
}

/**
 * Grabs all projects from the indexeddb and returns them
 * @author Eric Webb <ewebb@factorearth.com>
 * @returns All projects from the indexedDb
 */
export async function getProjects(): Promise<ProjectForResolver[]> {
	const projects = await db.project.toArray();
	return projects;
}

/**
 * Queries the count of all projects, multiplies the count by the average size, and returns that value
 * @author Eric Webb <ewebb@factorearth.com>
 * @returns The filesize, in bytes, of all projects
 */
export async function getTotalProjectDiskSize(): Promise<number> {
	const number = await db.project.count();
	const AVERAGE_DOCUMENT_SIZE = 182.21;
	return number * AVERAGE_DOCUMENT_SIZE;
}
